import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRightCircle, X } from 'lucide-react';
import type { Lead } from '../../../lib/leadsApi';
import { useConvertLead } from '../../../hooks/useLeads';
import { useClients } from '../../../hooks/useClients';
import { formatCurrency } from '../leadConstants';

/**
 * CLE-192 — "Convert to Project" dialog (spec 6.4).
 *
 * Pre-filled from the lead: project name (title), an optional code, the linked
 * client (read-only — inherited by the backend), and budget (from value). On
 * confirm the backend creates the Project, links convertedToProjectId, moves the
 * lead to its WON stage and logs the conversion. The page then shows the
 * converted banner.
 */
export function ConvertToProjectDialog({
  lead,
  onClose,
  onConverted,
}: {
  lead: Lead;
  onClose: () => void;
  onConverted: (projectId: string) => void;
}) {
  const convert = useConvertLead();
  const { data: clients } = useClients({ limit: 200 });
  const [projectName, setProjectName] = useState(lead.title);
  const [projectCode, setProjectCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const clientName = lead.clientId
    ? (clients?.data.find((c) => c.id === lead.clientId)?.name ?? 'Client asociat')
    : null;

  function submit() {
    setError(null);
    const name = projectName.trim();
    if (!name) {
      setError('Numele proiectului este obligatoriu.');
      return;
    }
    convert.mutate(
      {
        id: lead.id,
        projectName: name,
        ...(projectCode.trim() ? { projectCode: projectCode.trim() } : {}),
      },
      {
        onSuccess: (updated) => {
          if (updated.convertedToProjectId) onConverted(updated.convertedToProjectId);
          onClose();
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Conversia a eșuat.'),
      },
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-gray-200 bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="flex items-center gap-2 text-[15px] font-semibold text-gray-900">
            <ArrowRightCircle size={16} className="text-brand-600" /> Convertește în proiect
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-800"
            aria-label="Închide"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3 px-4 py-4">
          <Field label="Nume proiect">
            <input
              autoFocus
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[13px] text-gray-800 focus:border-brand-600 focus:outline-none"
            />
          </Field>

          <Field label="Cod proiect (opțional)">
            <input
              value={projectCode}
              onChange={(e) => setProjectCode(e.target.value)}
              placeholder="ex. ACME-2025"
              className="w-full rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-brand-600 focus:outline-none"
            />
          </Field>

          <Field label="Client">
            <div className="rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[13px] text-gray-700">
              {clientName ?? <span className="text-gray-400">Niciun client asociat</span>}
            </div>
          </Field>

          <Field label="Budget">
            <div className="rounded border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[13px] text-gray-700">
              {lead.value != null ? (
                formatCurrency(lead.value, lead.currency)
              ) : (
                <span className="text-gray-400">Nicio valoare setată</span>
              )}
            </div>
          </Field>

          <p className="text-[12px] text-gray-400">
            Conversia mută acest lead în etapa Câștigat și păstrează comentariile și activitatea.
          </p>

          {error && <p className="text-[12px] text-red-500">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-[13px] text-gray-500 hover:bg-gray-200"
          >
            Anulează
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={convert.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {convert.isPending ? 'Se convertește…' : 'Convertește'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[12px] font-medium text-gray-500">{label}</label>
      {children}
    </div>
  );
}
